import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

import type { TrackVersion } from '../../../types';
import { autoPluralize, countryRenderer } from '../../Dashboard/renderers';
import { QuickList } from './QuickList';

function trendClassName(trend: number): string {
  if (trend > 0) {
    return 'text-success-default';
  }

  if (trend < 0) {
    return 'text-error-default';
  }

  return 'text-gray-600';
}

interface Info {
  trend: number;
  added: string[];
  removed: string[];
  single: string;
  plural: string;
  extra?: string;
}

export function VersionsInfo(props: { versions: TrackVersion[] }) {
  const { t } = useTranslation('label-perf');

  const { versions } = props;

  const [expandedVersions, setExpandedVersions] = useState<number[]>([]);

  const toggleVersionExpand = (index: number): void => {
    setExpandedVersions((prevExpandedVersions) =>
      prevExpandedVersions.includes(index)
        ? prevExpandedVersions.filter((i) => i !== index)
        : [...prevExpandedVersions, index],
    );
  };

  const getAdded = (newArr: string[], oldArr: string[]): string[] => {
    return newArr.filter((c) => !oldArr.includes(c));
  };

  const getRemoved = (newArr: string[], oldArr: string[]): string[] => {
    return oldArr.filter((c) => !newArr.includes(c));
  };

  return (
    <div className="mt-lg">
      <h2 className="mb-md text-body-md font-bold text-white">
        {t('body.trackDistributionHistory')}
      </h2>
      <div className="flex flex-col-reverse">
        {versions
          .slice()
          .reverse()
          .map((v, index, all) => {
            const expanded = expandedVersions.includes(index);

            const info: Info[] = [
              {
                trend:
                  v.partners.length -
                  (versions[index - 1]?.partners.length ?? 0),
                single: 'partner',
                plural: 'partners',
                added: getAdded(
                  v.partners,
                  versions[index - 1]?.partners ?? [],
                ),
                removed: getRemoved(
                  v.partners,
                  versions[index - 1]?.partners ?? [],
                ),
              },
              {
                trend:
                  v.territories.length -
                  (versions[index - 1]?.territories.length ?? 0),
                single: 'territory',
                plural: 'territories',
                extra: 'All',
                added: getAdded(
                  v.territories,
                  versions[index - 1]?.territories ?? [],
                ),
                removed: getRemoved(
                  v.territories,
                  versions[index - 1]?.territories ?? [],
                ),
              },
              {
                trend:
                  v.appleTerrs.length -
                  (versions[index - 1]?.appleTerrs.length ?? 0),
                single: 'territory',
                plural: 'territories',
                extra: 'Apple',
                added: getAdded(
                  v.appleTerrs,
                  versions[index - 1]?.appleTerrs ?? [],
                ),
                removed: getRemoved(
                  v.appleTerrs,
                  versions[index - 1]?.appleTerrs ?? [],
                ),
              },
              {
                trend:
                  v.spotifyTerrs.length -
                  (versions[index - 1]?.spotifyTerrs.length ?? 0),
                single: 'territory',
                plural: 'territories',
                extra: 'Spotify',
                added: getAdded(
                  v.spotifyTerrs,
                  versions[index - 1]?.spotifyTerrs ?? [],
                ),
                removed: getRemoved(
                  v.spotifyTerrs,
                  versions[index - 1]?.spotifyTerrs ?? [],
                ),
              },
            ];

            let verName = v.ver === 1 ? 'Initial' : `Ver. ${v.ver}`;

            if (all.length === index + 1) {
              verName = 'Current';
            }

            return (
              <div key={`${index}_${v.ver}`}>
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={`version-toggle-${index}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      toggleVersionExpand(index);
                    }
                  }}
                  className={cn(
                    'group mb-md flex cursor-pointer select-none items-center rounded-sm border px-lg py-md border-emphasis-subtle',
                    expanded && 'text-primary-pressed',
                  )}
                  onClick={() => toggleVersionExpand(index)}
                >
                  <Icon
                    className={cn(
                      'mr-sm text-body-xs text-white',
                      expanded && 'text-primary-pressed',
                    )}
                    size="sm"
                    type={expanded ? 'chevron-up' : 'chevron-down'}
                  />
                  <span className="ml-xs font-semibold">{verName}</span>
                  <span className="ml-xs text-body-sm font-light">
                    ({v.date.toLocaleDateString()}){!expanded ? ': ' : ''}
                  </span>
                  {!expanded && (
                    <>
                      {info
                        .flatMap((_t) => {
                          const r: Info[] = [];

                          if (_t.added.length > 0) {
                            r.push({ ..._t, trend: _t.added.length });
                          }

                          if (_t.removed.length > 0) {
                            r.push({ ..._t, trend: -_t.removed.length });
                          }

                          return r;
                        })
                        .map((inf, idx, _all) => (
                          <span
                            data-testid={`collapsed-summary-${index}-${idx}`}
                            className="ml-xs text-body-xs font-light"
                            key={idx}
                          >
                            <b
                              className={cn(
                                'font-semibold',
                                'mr-xs',
                                trendClassName(inf.trend),
                              )}
                            >
                              {index > 0 && inf.trend > 0 ? '+' : ''}
                              {inf.trend}
                            </b>{' '}
                            {autoPluralize(inf.trend, inf.single, inf.plural)}
                            {inf.extra ? ` (${inf.extra})` : null}
                            {inf !== _all[_all.length - 1] && ', '}
                          </span>
                        ))}
                    </>
                  )}
                </div>
                {expanded && index === 0 ? (
                  <div className="p-md">
                    <div className="grid grid-cols-2 gap-lg">
                      <div className="rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
                        <div className="mb-md text-body-sm font-semibold">
                          {v.partners.length}{' '}
                          {autoPluralize(
                            v.partners.length,
                            'partner',
                            'partners',
                          )}
                        </div>
                        <QuickList className="mb-lg" values={v.partners} />
                      </div>
                      <div className="rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
                        <div className="mb-md text-body-sm font-semibold">
                          {v.territories.length}{' '}
                          {autoPluralize(
                            v.territories.length,
                            'territory',
                            'territories',
                          )}
                        </div>
                        <QuickList
                          className="mb-lg"
                          values={v.territories.map((c) =>
                            countryRenderer(c, 'detailed'),
                          )}
                        />
                      </div>
                      <div className="rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
                        <div className="mb-md text-body-sm font-semibold">
                          {v.appleTerrs.length}{' '}
                          {autoPluralize(
                            v.appleTerrs.length,
                            'territory',
                            'territories',
                          )}{' '}
                          {t('Apple')}
                        </div>
                        <QuickList
                          className="mb-lg"
                          values={v.appleTerrs.map((c) =>
                            countryRenderer(c, 'detailed'),
                          )}
                        />
                      </div>
                      <div className="rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
                        <div className="mb-md text-body-sm font-semibold">
                          {v.spotifyTerrs.length}{' '}
                          {autoPluralize(
                            v.spotifyTerrs.length,
                            'territory',
                            'territories',
                          )}{' '}
                          {t('Spotify')}
                        </div>
                        <QuickList
                          className="mb-lg"
                          values={v.spotifyTerrs.map((c) =>
                            countryRenderer(c, 'detailed'),
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
                {expanded && index > 0 ? (
                  <div className="grid grid-cols-2 gap-lg">
                    {info
                      .flatMap((_t) => {
                        const r: Info[] = [];

                        if (_t.added.length > 0) {
                          r.push({ ..._t, trend: _t.added.length });
                        }

                        if (_t.removed.length > 0) {
                          r.push({ ..._t, trend: -_t.removed.length });
                        }

                        return r;
                      })
                      .map((inf) => (
                        <div className="p-md" key={inf.single}>
                          <div className="mb-lg rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
                            <div className="mb-md text-body-sm font-semibold">
                              <b
                                className={cn(
                                  'font-semibold',
                                  'mr-xs',
                                  trendClassName(inf.trend),
                                )}
                              >
                                {index > 0 && inf.trend > 0 ? '+' : ''}
                                {inf.trend}
                              </b>{' '}
                              {autoPluralize(inf.trend, inf.single, inf.plural)}
                              {inf.extra ? ` (${inf.extra})` : null}
                            </div>
                            <QuickList
                              className="mb-lg"
                              values={inf.trend > 0 ? inf.added : inf.removed}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
}

VersionsInfo.displayName = 'VersionsInfo';
